import { DateTime } from "luxon";
import { SchoolHoursModel, SchoolYearType, ShortenedDays } from "model";

import { TabItem } from "~components/Molecules/Tabs/Tabs";

const ACADEMIC_YEAR_START_MONTH = 7;

type CalcTimeProps = {
  hours: SchoolHoursModel;
  time: DateTime;
  type: "start" | "end";
};

/**
 * Calculates the start or end time for school hours on a specific day.
 * Converts a 4-digit time format (e.g., 830 for 8:30) to ISO string format.
 *
 * @param props - Configuration object for time calculation
 * @param props.hours - School hours model containing time data for each day
 * @param props.time - DateTime object representing the target date
 * @param props.type - Whether to calculate "start" or "end" time
 * @returns ISO string representation of the calculated time, or null if conversion fails
 *
 * @example
 * ```ts
 * const hours = { mon: { start: 830, end: 1530 } };
 * const time = DateTime.local(2024, 9, 2); // Monday
 * const startTime = calcHours({ hours, time, type: "start" });
 * // Returns ISO string for 8:30 AM on that Monday
 * ```
 */
export const calcHours = (props: CalcTimeProps) => {
  const { hours, time, type } = props;

  const weekday = time.weekdayShort.toLowerCase() as ShortenedDays;

  const newDateTime = time
    .set({
      hour: parseInt(hours[weekday][type].toString().padStart(4, "0").slice(0, 2)),
      minute: parseInt(hours[weekday][type].toString().padStart(4, "0").slice(2, 5)),
    })
    .toISO();

  return newDateTime;
};

/**
 * Generates academic year periods based on a reference date and start month.
 * Returns current and next academic year periods (e.g., "2024-2025", "2025-2026").
 *
 * @param datetime - Reference date to determine current academic year
 * @param startMonth - Month when academic year starts (1-12), defaults to July (7)
 * @returns Array of two TabItem objects representing current and next academic years
 *
 * @example
 * ```ts
 * const datetime = DateTime.local(2024, 9, 15); // September 2024
 * const years = getAcademicYears(datetime, 7); // July start
 * // Returns: [{ label: "2024-2025", value: "2024-2025", isDisabled: false }, ...]
 * ```
 */
export function getAcademicYears(
  datetime: DateTime,
  startMonth: number = ACADEMIC_YEAR_START_MONTH,
): TabItem<string>[] {
  const now = datetime;

  const currentStartYear = now.month >= startMonth ? now.year : now.year - 1;
  const currentEndYear = currentStartYear + 1;

  const nextStartYear = currentStartYear + 1;
  const nextEndYear = nextStartYear + 1;

  return [
    {
      label: `${currentStartYear}-${currentEndYear}`,
      value: `${currentStartYear}-${currentEndYear}`,
      isDisabled: false,
    },
    {
      label: `${nextStartYear}-${nextEndYear}`,
      value: `${nextStartYear}-${nextEndYear}`,
      isDisabled: false,
    },
  ];
}

/**
 * Creates empty academic year ranges from a list of academic year tab items.
 * Each range is initialized with empty start and end dates.
 *
 * @param academicYears - Array of academic year tab items
 * @returns Record mapping academic year strings to empty SchoolYearType objects
 *
 * @example
 * ```ts
 * const tabItems = [{ label: "2024-2025", value: "2024-2025", isDisabled: false }];
 * const ranges = getAcademicYearRanges(tabItems);
 * // Returns: { "2024-2025": { id: "2024-2025", start: "", end: "" } }
 * ```
 */
export function getAcademicYearRanges(
  academicYears: TabItem<string>[],
): Record<string, SchoolYearType> {
  return Object.fromEntries(
    academicYears.map((yearItem) => [yearItem.value, { id: yearItem.value, start: "", end: "" }]),
  );
}

/**
 * Determines the academic year period (e.g., "2024-2025") for a given school year.
 * Uses the start date to calculate which academic year the school year belongs to.
 *
 * @param schoolYear - School year object containing start and end dates
 * @param startMonth - Month when academic year starts (1-12), defaults to July (7)
 * @returns Academic year period string (e.g., "2024-2025") or null if start date is invalid
 *
 * @example
 * ```ts
 * const schoolYear = {
 *   id: "1",
 *   start: "2024-08-15T08:00:00.000Z",
 *   end: "2025-06-15T15:00:00.000Z"
 * };
 * const period = getAcademicYearPeriod(schoolYear);
 * // Returns: "2024-2025"
 * ```
 */
export function getAcademicYearPeriod(
  schoolYear: SchoolYearType,
  startMonth: number = ACADEMIC_YEAR_START_MONTH,
): string | null {
  if (!schoolYear.start) return null;

  const startDate = DateTime.fromISO(schoolYear.start);
  if (!startDate.isValid) return null;

  const academicStartYear = startDate.month >= startMonth ? startDate.year : startDate.year - 1;
  const academicEndYear = academicStartYear + 1;

  return `${academicStartYear}-${academicEndYear}`;
}

/**
 * Maps existing school years to their corresponding academic year periods.
 * Combines empty academic year ranges with existing school year data where periods match.
 *
 * @param existingYears - Array of existing school years or undefined
 * @param academicYears - Array of academic year tab items to create base structure
 * @param startMonth - Month when academic year starts (1-12), defaults to July (7)
 * @returns Record mapping academic year strings to SchoolYearType objects
 *
 * @example
 * ```ts
 * const existingYears = [{
 *   id: "year1",
 *   start: "2024-08-15T08:00:00.000Z",
 *   end: "2025-06-15T15:00:00.000Z"
 * }];
 * const academicYears = [{ label: "2024-2025", value: "2024-2025", isDisabled: false }];
 * const mapped = mapSchoolYearsToAcademicPeriods(existingYears, academicYears);
 * // Returns: { "2024-2025": { id: "year1", start: "2024-08-15...", end: "2025-06-15..." } }
 * ```
 *
 * @remarks
 * - If existingYears is undefined or empty, returns empty ranges for all academic years
 * - School years with invalid start dates are ignored
 * - If multiple school years map to the same academic period, the last one wins
 * - School years that don't match any academic period are ignored
 */
export function mapSchoolYearsToAcademicPeriods(
  existingYears: SchoolYearType[] | undefined,
  academicYears: TabItem<string>[],
  startMonth: number = ACADEMIC_YEAR_START_MONTH,
): Record<string, SchoolYearType> {
  const academicYearRanges = getAcademicYearRanges(academicYears);

  if (!existingYears || existingYears.length === 0) {
    return academicYearRanges;
  }

  existingYears.forEach((schoolYear) => {
    const academicPeriod = getAcademicYearPeriod(schoolYear, startMonth);

    if (academicPeriod && academicYearRanges[academicPeriod]) {
      academicYearRanges[academicPeriod] = {
        id: schoolYear.id,
        start: schoolYear.start,
        end: schoolYear.end,
      };
    }
  });

  return academicYearRanges;
}

/**
 * Determines if a school year has existing data (UUID id) or is just a placeholder (academic year string id)
 * Placeholder IDs are academic year strings like "2024-2025" (9 chars)
 * Existing data has UUIDs which are much longer and contain hyphens in specific positions
 *
 * @param schoolYear - School year object to check
 * @param academicYearString - Academic year string (e.g., "2024-2025") to compare against
 * @returns true if the school year has existing data (UUID id), false if it's a placeholder
 *
 * @example
 * ```ts
 * const existingSchoolYear = { id: "uuid-123-abc", start: "2024-08-15...", end: "2025-06-15..." };
 * const placeholder = { id: "2024-2025", start: "", end: "" };
 *
 * hasExistingSchoolYearData(existingSchoolYear, "2024-2025"); // true
 * hasExistingSchoolYearData(placeholder, "2024-2025");        // false
 * ```
 */
export function hasExistingSchoolYearData(
  schoolYear: SchoolYearType,
  academicYearString: string,
): boolean {
  if (!schoolYear?.id || !schoolYear.start || !schoolYear.end) {
    return false;
  }

  // If the ID is the same as the academic year string, it's a placeholder
  // If it's different (should be a UUID), it's existing data
  return schoolYear.id !== academicYearString;
}

/**
 * Calculates academic year date range from tab value or school year.
 * Returns start and end dates for the academic year period.
 *
 * @param tabValue - Academic year string (e.g., "2024-2025") or empty string
 * @param schoolYear - School year object to derive period from if tabValue is empty
 * @returns Object with start and end date strings, or null if unable to calculate
 *
 * @example
 * ```ts
 * const dateRange = getAcademicYearDateRange("2024-2025");
 * // Returns: { start: "2024-07-01", end: "2025-06-30" }
 * ```
 */
export function getAcademicYearDateRange(
  tabValue: string,
  schoolYear?: SchoolYearType,
): { start: string; end: string } | null {
  if (!tabValue && !schoolYear) {
    return null;
  }

  let academicYearString: string;

  if (!tabValue) {
    const academicYearPeriod = getAcademicYearPeriod(schoolYear!);
    if (!academicYearPeriod) {
      return null;
    }
    academicYearString = academicYearPeriod;
  } else {
    academicYearString = tabValue;
  }

  const yearMatch = academicYearString.match(/(\d{4})-(\d{4})/);
  if (!yearMatch) {
    return null;
  }

  const [, startYearStr, endYearStr] = yearMatch;

  const startMonth = ACADEMIC_YEAR_START_MONTH.toString().padStart(2, "0");
  const endMonth = (ACADEMIC_YEAR_START_MONTH - 1 || 12).toString().padStart(2, "0");

  return {
    start: `${startYearStr}-${startMonth}-01`,
    end: `${endYearStr}-${endMonth}-30`,
  };
}
